import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import ApiClient, { ApiError } from '../utils/apiClient';

const PlanSelection = ({ onNext, onBack, data, setData }) => {
  const { getToken } = useAuth();
  const [apiClient] = useState(() => new ApiClient(getToken));
  
  // State management
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(data.selectedPlan || null);
  const [fundingAmount, setFundingAmount] = useState(data.fundingAmount || 0);
  const [promoCode, setPromoCode] = useState(data.promoCode || '');
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [promoType, setPromoType] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [userContext, setUserContext] = useState(null);

  // Load enhanced plans on component mount
  useEffect(() => {
    loadEnhancedPlans();
  }, []);

  // Update funding amount when plan changes
  useEffect(() => {
    if (selectedPlan && plans.length > 0) {
      const plan = plans.find(p => p.id === selectedPlan);
      if (plan) {
        const suggestedAmount = plan.suggested_funding;
        setFundingAmount(suggestedAmount);
        setData({
          ...data,
          selectedPlan,
          fundingAmount: suggestedAmount,
          planDetails: plan
        });
      }
    }
  }, [selectedPlan, plans]);

  const loadEnhancedPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.get('/api/billing/plans');
      
      setPlans(response.plans || []);
      setUserContext(response.user_context || {});
      
      // Set recommended plan as default if none selected
      if (!selectedPlan && response.plans?.length > 0) {
        const recommendedPlan = response.plans.find(p => p.recommended) || response.plans[0];
        setSelectedPlan(recommendedPlan.id);
      }
      
    } catch (err) {
      console.error('Error loading plans:', err);
      setError(err instanceof ApiError ? err.message : 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const applyPromoCode = async () => {
    if (!promoCode.trim() || !selectedPlan) return;
    
    try {
      const response = await apiClient.post('/api/admin/promo/validate', {
        promo_code: promoCode,
        plan_id: selectedPlan
      });
      
      setPromoDiscount(response.discount);
      setPromoType(response.type);
      setPromoApplied(true);
      
      // Recalculate funding amount with discount
      const plan = plans.find(p => p.id === selectedPlan);
      if (plan) {
        let discountedAmount = plan.suggested_funding;
        if (response.type === 'percentage') {
          discountedAmount = Math.ceil(discountedAmount * (1 - response.discount / 100));
        } else {
          discountedAmount = Math.max(1000, discountedAmount - (response.discount * 100)); // Minimum $10
        }
        setFundingAmount(discountedAmount);
      }
      
    } catch (err) {
      console.error('Error applying promo code:', err);
      setError('Invalid promo code or unable to apply discount');
    }
  };

  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
    setPromoApplied(false);
    setPromoDiscount(0);
    setPromoType('');
    setPromoCode('');
  };

  const handleStripePayment = async () => {
    if (!selectedPlan || !fundingAmount) return;
    
    try {
      setProcessingPayment(true);
      setError(null);
      
      const paymentData = {
        plan_id: selectedPlan,
        funding_amount_cents: fundingAmount,
        ...(promoApplied && { promo_code: promoCode })
      };
      
      const response = await apiClient.post('/api/onboarding/plan-select', paymentData);
      
      if (response.checkout_url) {
        // Store current state before redirecting
        setData({
          ...data,
          selectedPlan,
          fundingAmount,
          promoCode: promoApplied ? promoCode : '',
          planDetails: plans.find(p => p.id === selectedPlan)
        });
        
        // Redirect to Stripe checkout
        window.location.href = response.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
      
    } catch (err) {
      console.error('Error processing payment:', err);
      setError(err instanceof ApiError ? err.message : 'Failed to process payment');
      setProcessingPayment(false);
    }
  };

  const canProceed = () => {
    return selectedPlan && fundingAmount >= 1000; // Minimum $10
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300 dark:text-gray-600">Loading plans...</p>
        </div>
      </div>
    );
  }

  if (error && plans.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">
          <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-lg font-medium">Unable to load plans</p>
          <p className="text-sm text-gray-600 mt-2 dark:text-gray-300 dark:text-gray-600">{error}</p>
        </div>
        <button
          onClick={loadEnhancedPlans}
          className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 dark:text-white"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
          <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2 dark:text-white">Choose Your Plan</h2>
        <p className="text-gray-600 max-w-2xl mx-auto dark:text-gray-300 dark:text-gray-600">
          Select the perfect plan for your needs. All plans include AI voice calls, real-time summaries, and integrations.
        </p>
      </div>

      {/* User Context Info */}
      {userContext && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-1">Account Status</p>
              <p>Current wallet balance: <span className="font-semibold">${((userContext.wallet_balance || 0) / 100).toFixed(2)}</span></p>
              {userContext.has_billing_setup && (
                <p className="text-blue-600">✓ Billing preferences already configured</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`relative bg-white rounded-xl shadow-lg p-6 cursor-pointer transition-all duration-200 ${
              selectedPlan === plan.id
                ? 'ring-2 ring-teal-500 bg-teal-50 scale-105'
                : 'hover:shadow-xl hover:scale-102'
            } ${plan.recommended ? 'ring-2 ring-green-400' : ''}`}
            onClick={() => handlePlanSelect(plan.id)}
          >
            {/* Recommended Badge */}
            {plan.recommended && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold dark:text-white">
                  Recommended
                </span>
              </div>
            )}

            {/* Popular Badge */}
            {plan.is_popular && !plan.recommended && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-semibold dark:text-white">
                  Most Popular
                </span>
              </div>
            )}

            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2 dark:text-white">{plan.name}</h3>
              <div className="text-3xl font-bold text-teal-600 mb-2">
                ${(plan.monthly_fee / 100).toFixed(0)}
                <span className="text-lg font-normal text-gray-500">/month</span>
              </div>
              
              {/* First Month Discount */}
              {plan.savings_vs_monthly > 0 && (
                <div className="text-sm font-semibold text-green-600">
                  First month: ${(plan.first_month_fee / 100).toFixed(0)} 
                  <span className="text-xs text-gray-500 ml-1">
                    (Save ${(plan.savings_vs_monthly / 100).toFixed(0)})
                  </span>
                </div>
              )}
            </div>

            {/* Features */}
            <ul className="space-y-3 mb-6">
              {plan.features?.map((feature, index) => (
                <li key={index} className="flex items-start">
                  <svg className="w-5 h-5 text-teal-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-gray-600 dark:text-gray-300 dark:text-gray-600">{feature}</span>
                </li>
              ))}
            </ul>

            {/* Suggested Funding */}
            <div className="bg-gray-50 rounded-lg p-3 mb-4 dark:bg-gray-900">
              <div className="text-xs text-gray-500 mb-1">Suggested initial funding:</div>
              <div className="text-lg font-semibold text-gray-900 dark:text-white">
                ${(plan.suggested_funding / 100).toFixed(2)}
              </div>
              <div className="text-xs text-gray-500">
                Includes 3 months of service
              </div>
            </div>

            {/* Selection Indicator */}
            {selectedPlan === plan.id && (
              <div className="absolute top-4 right-4">
                <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white dark:text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Selected Plan Configuration */}
      {selectedPlan && (
        <div className="bg-white rounded-lg shadow-lg p-6 dark:bg-gray-800">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 dark:text-white">Configure Your Plan</h3>
          
          {/* Plan Summary */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6 dark:bg-gray-900">
            <div className="flex justify-between items-center mb-2">
              <span className="font-medium text-gray-900 dark:text-white">Selected Plan:</span>
              <span className="text-gray-700">{plans.find(p => p.id === selectedPlan)?.name}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-300 dark:text-gray-600">Monthly Cost:</span>
              <span className="text-gray-700">
                ${(plans.find(p => p.id === selectedPlan)?.monthly_fee / 100).toFixed(2)}
              </span>
            </div>
          </div>

          {/* Promo Code Section */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Promo Code (Optional)
            </label>
            <div className="flex gap-3">
              <input
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                placeholder="Enter promo code"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:border-gray-600"
                disabled={promoApplied}
              />
              <button
                onClick={applyPromoCode}
                disabled={promoApplied || !promoCode.trim() || !selectedPlan}
                className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed dark:text-white"
              >
                {promoApplied ? 'Applied' : 'Apply'}
              </button>
            </div>
            {promoApplied && (
              <p className="text-green-600 text-sm mt-2">
                ✓ Promo code applied! {promoType === 'percentage' ? `${promoDiscount}% off` : `$${(promoDiscount).toFixed(2)} off`}
              </p>
            )}
          </div>

          {/* Funding Amount */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Initial Wallet Funding
            </label>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <input
                  type="number"
                  min="10"
                  step="1"
                  value={(fundingAmount / 100).toFixed(2)}
                  onChange={(e) => {
                    const amount = Math.max(10, parseFloat(e.target.value) || 10);
                    setFundingAmount(Math.ceil(amount * 100));
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:border-gray-600"
                />
              </div>
              <span className="text-gray-600 dark:text-gray-300 dark:text-gray-600">USD</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Minimum $10.00. Recommended amount covers 3 months of service.
            </p>
          </div>

          {/* Wallet Funding Explanation */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div className="text-sm text-blue-700">
                <h4 className="font-semibold mb-2">How Wallet Funding Works</h4>
                <ul className="space-y-1">
                  <li>• Your wallet will be funded with ${(fundingAmount / 100).toFixed(2)} to cover your plan and usage</li>
                  <li>• Monthly plan costs are automatically deducted from your wallet</li>
                  <li>• Auto-refill will trigger when your balance gets low</li>
                  <li>• You can refund unused balance anytime</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              onClick={handleStripePayment}
              disabled={!canProceed() || processingPayment}
              className="flex-1 py-3 px-6 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200 dark:text-white"
            >
              {processingPayment ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Processing...
                </div>
              ) : (
                `Fund Wallet & Continue`
              )}
            </button>
            
            {onBack && (
              <button
                onClick={onBack}
                disabled={processingPayment}
                className="px-6 py-3 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 dark:bg-gray-900 dark:text-gray-300 dark:text-gray-600 dark:border-gray-600"
              >
                Back
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanSelection;
